import './ContactsEmailWall.scss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { UserCircle, ArrowsClockwise, CircleNotch, Clock, Sparkle } from '@phosphor-icons/react';
import { siGmail } from 'simple-icons';
import { API_BASE_URL, API_ORIGIN_URL } from '../../../../api/config';
import fetchService from '../../../../services/fetchService';
import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger('CONTACTS_EMAIL_WALL');

function GmailIcon({ size = 18, className }: { size?: number; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden className={className}>
      <path d={siGmail.path} />
    </svg>
  );
}

interface ContactsEmailWallProps {
  onConnected: () => void;
}

export const ContactsEmailWall: React.FC<ContactsEmailWallProps> = ({ onConnected }) => {
  const [connecting, setConnecting] = useState(false);
  const [polling, setPolling] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const startPolling = useCallback(() => {
    setPolling(true);
    pollRef.current = setInterval(async () => {
      try {
        const result = await fetchService(`${API_BASE_URL}/contacts-app/email-status`);
        if (result?.connected) {
          stopPolling();
          setPolling(false);
          setConnecting(false);
          onConnected();
        }
      } catch {
        // Keep polling silently
      }
    }, 2000);
  }, [stopPolling, onConnected]);

  const handleConnect = useCallback(async () => {
    setConnecting(true);
    try {
      const result = await fetchService(`${API_ORIGIN_URL}/api/email/oauth/gmail/url`);
      const url = result?.authorization_url;
      if (!url) throw new Error('No authorization URL returned');

      const popup = window.open(url, '_blank', 'popup,width=520,height=660,left=200,top=100');
      if (!popup) {
        window.location.href = url;
        return;
      }

      log.info('[ContactsEmailWall] Opened OAuth popup, starting status poll');
      startPolling();
    } catch (error) {
      log.error('[ContactsEmailWall] Failed to initiate Gmail connection', error);
      setConnecting(false);
    }
  }, [startPolling]);

  return (
    <div className="contacts-email-wall">
      <div className="contacts-email-wall__card">
        <div className="contacts-email-wall__header">
          <GmailIcon size={28} className="contacts-email-wall__gmail-icon" />
          <div>
            <h2 className="contacts-email-wall__title">Connect your Gmail</h2>
            <p className="contacts-email-wall__subtitle">Required to use the Contacts app</p>
          </div>
        </div>

        <p className="contacts-email-wall__body">
          Contacts reads your inbox to automatically build and maintain your network.
        </p>

        <ul className="contacts-email-wall__features">
          <li>
            <span className="contacts-email-wall__feature-icon">
              <UserCircle size={14} weight="duotone" />
            </span>
            Auto-discovers people you email regularly
          </li>
          <li>
            <span className="contacts-email-wall__feature-icon">
              <Clock size={14} weight="duotone" />
            </span>
            Tracks your last interaction with each person
          </li>
          <li>
            <span className="contacts-email-wall__feature-icon">
              <Sparkle size={14} weight="duotone" />
            </span>
            Suggests when to follow up, updated hourly
          </li>
        </ul>

        <div className="contacts-email-wall__footer">
          <button
            className="contacts-email-wall__btn"
            onClick={handleConnect}
            disabled={connecting || polling}
          >
            {polling ? (
              <>
                <ArrowsClockwise size={15} className="contacts-email-wall__spinner" />
                Waiting for connection…
              </>
            ) : connecting ? (
              <>
                <CircleNotch size={15} className="contacts-email-wall__spinner" />
                Opening Gmail…
              </>
            ) : (
              <>
                <GmailIcon size={15} className="contacts-email-wall__btn-icon" />
                Connect Gmail
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
